import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { NgbActiveModal, NgbCalendar, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'src/app/classes/Subscription';
import { SubscriptionType } from 'src/app/enums/SubscriptionType';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Page } from 'src/app/classes/Page';
import { CoursesService } from 'src/app/services/courses.service';
import { addDays } from 'date-fns';
import { dateRangeValidator, timeRangeValidator } from 'src/app/components/subscription/subscription-edit/DateTimeRangeValidator';


@Component({
  selector: 'app-subscription-edit',
  styleUrls: ['./subscription-edit.component.scss'],
  templateUrl: './subscription-edit.component.html',
  encapsulation: ViewEncapsulation.None
})
export class SubscriptionEditComponent implements OnInit {

  @Input() subscription: Subscription;
  @ViewChild('selectedSubscriptionTypeSelect', { static: false }) selectedSubscriptionTypeSelect: NgSelectComponent;
  destroy = new Subject<any>();
  currentDialog = null;
  errorMessage: string;
  subscriptionType = SubscriptionType;
  subscriptionTypes = Object.entries(SubscriptionType).filter(([key]) => isNaN(Number(key))).map(([key, value]) => {
    return {
      key,
      value
    };
  });

  public subscriptionForm: FormGroup;
  public minDate: NgbDateStruct;
  public courses;

  constructor(private activeModal: NgbActiveModal,
              private calendar: NgbCalendar,
              private coursesService: CoursesService,
              private formBuilder: FormBuilder) {
    this.minDate = this.calendar.getToday();
  }

  private rebuildPrices(): void {
    const count = this.periods.length;
    const currentPrices = this.prices.controls;

    const newPricesArray = this.formBuilder.array<FormGroup<{
      selected: FormControl<boolean>,
      periodCount: FormControl<number>,
      amount: FormControl<number | null>
    }>>([]);

    for (let i = 0; i < count; i++) {
      const existing = currentPrices[i];

      let selected = false;
      let amount: number | null = null;

      if (existing) {
        selected = existing.get('selected')?.value ?? false;
        amount = existing.get('amount')?.value ?? null;
      }

      const group = this.formBuilder.group({
        selected: this.formBuilder.control(selected, { nonNullable: true }),
        periodCount: this.formBuilder.control(i + 1, { nonNullable: true }),
        amount: this.formBuilder.control(
          { value: amount, disabled: !selected },
          Validators.required
        )
      });

      group.get('selected')?.valueChanges.subscribe((checked: boolean) => {
        const priceControl = group.get('amount');
        checked ? priceControl?.enable() : priceControl?.disable();
      });

      newPricesArray.push(group);
    }

    this.createCampRequest.setControl('prices', newPricesArray);
  }

  patchPricesFromEntity(pricesData: { periodCount: number; amount: number }[]): void {
    this.rebuildPrices();

    pricesData.forEach(({ periodCount, amount }) => {
      const index = periodCount - 1;
      const priceGroup = this.prices.at(index);
      if (priceGroup) {
        priceGroup.get('selected')?.setValue(true);
        priceGroup.get('amount')?.setValue(amount);
        priceGroup.get('amount')?.enable();
      }
    });
  }


  ngOnInit(): void {
    this.getCourses();
    if (this.subscription) {
      this.subscriptionForm = this.formBuilder.group({
        id: [this.subscription.id],
        price: [this.subscription.price, Validators.required],
        lessonsCount: [this.subscription.lessonsCount, Validators.required],
        description: [this.subscription.description, Validators.required],
        descriptionHebrew: [this.subscription.descriptionHebrew, Validators.required],
        nameHebrew: [this.subscription.nameHebrew, Validators.required],
        name: [this.subscription.name, Validators.required],
        isOnline: [this.subscription.isOnline, Validators.required],
        subscriptionType: [
          this.getSubscriptionType(this.subscription), Validators.required
        ],
        bundleId: [this.subscription.bundleId],
        createCampRequest: this.formBuilder.group({
          maxStudents: [100, Validators.required],
          minAge: [this.subscription.camp?.minAge || null, Validators.required],
          maxAge: [this.subscription.camp?.maxAge || null, Validators.required],
          periods: this.createPeriods(),
          prices: this.formBuilder.array([])
        }),
      });
    } else {
      this.subscriptionForm = this.formBuilder.group({
        id: [null],
        price: [0, Validators.required],
        lessonsCount: [0, Validators.required],
        nameHebrew: ['', Validators.required],
        name: ['', Validators.required],
        description: ['', Validators.required],
        descriptionHebrew: ['', Validators.required],
        isOnline: [true, Validators.required],
        subscriptionType: [this.getSubscriptionType(this.subscription), Validators.required],
        bundleId: [null],
        createCampRequest: this.formBuilder.group({
          maxStudents: [100, Validators.required],
          minAge: [null, Validators.required],
          maxAge: [null, Validators.required],
          periods: this.formBuilder.array([this.createPeriodGroup()]),
          prices: this.formBuilder.array([])
        }),
      });
    }
    // this.rebuildPrices();
    this.patchPricesFromEntity(this.subscription?.camp?.prices || []);
  }

  get createCampRequest(): FormGroup {
    return this.subscriptionForm.get('createCampRequest') as FormGroup;
  }

  get periods(): FormArray {
    return this.createCampRequest.get('periods') as FormArray;
  }

  get prices(): FormArray {
    return this.createCampRequest.get('prices') as FormArray;
  }

  createPeriods(): FormArray {
    const periods = this.subscription.camp?.periods.map((period: any) => this.formBuilder.group({
          periodStartDate: [period.periodStartDate, Validators.required],
          periodEndDate: [period.periodEndDate, Validators.required],
          periodStartTime: [period.periodStartTime, Validators.required],
          periodEndTime: [period.periodEndTime, Validators.required]
        }, {
          validators: [dateRangeValidator, timeRangeValidator]
        })) || [this.createPeriodGroup()];
    return this.formBuilder.array(periods);
  }

  createPeriodGroup(): FormGroup {
    const today = new Date().toISOString().split('T')[0];
    return this.formBuilder.group({
      periodStartDate: [today, Validators.required],
      periodEndDate: [null, Validators.required],
      periodStartTime: ['10:00', Validators.required],
      periodEndTime: ['11:00', Validators.required]
    }, {
      validators: [dateRangeValidator, timeRangeValidator]
    });
  }

  public addPeriod(): void {
    this.periods.push(this.createPeriodGroup());
    this.rebuildPrices();
  }

  public removePeriod(index: number): void {
    this.periods.removeAt(index);
    this.rebuildPrices();
  }

  public getCourses(): void {
    this.coursesService.getAll(0, 999, {
      endDateFrom: addDays(new Date(), 1)
    }).subscribe({
      next: (response: Page<any>) => {
        this.courses = response.content;
      },
      error: (error) => {
        this.errorMessage = error.error.error;
      },
      complete: () => {
      },
    });
  }

  public onSubscriptionTypeChange(): void {
    if (this.subscriptionForm.controls.subscriptionType.value.value !== this.subscriptionType.COURSE) {
      this.courses = [];
    } else {
      this.getCourses();
    }
  }

  public closeModal(sendData): void {
    this.activeModal.close(sendData);
  }

  public getSubscriptionType(subscription?: Subscription): { key: string, value: string | SubscriptionType } {
    if (subscription?.bundleId) {
      return this.subscriptionTypes.filter(t => t.value === this.subscriptionType.COURSE)[0];
    }
    if (subscription?.camp) {
      return this.subscriptionTypes.filter(t => t.value === this.subscriptionType.EVENT)[0];
    }
    return this.subscriptionTypes.filter(t => t.value === this.subscriptionType.REGULAR)[0];
  }
}
